import { forwardRef, ImgHTMLAttributes, useMemo } from "react";

/**
 * <FocusedImage>
 *
 * One source of truth for `object-position` cropping across the site.
 *
 * Why this exists:
 *   Hero images need different focal points per breakpoint to keep the
 *   subject in frame as the container aspect-ratio changes. We had this
 *   logic duplicated across HomeHero, the "Over ons" block on Home, and
 *   the Zomerkampen hero — each maintained its own className string.
 *   This component centralises the breakpoint-set and the data-focus
 *   labelling, while letting each call site pass its own focal points.
 *
 * Breakpoint set (locked, intentional — matches Tailwind defaults):
 *   base = <640px (mobile)
 *   sm   = ≥640px
 *   md   = ≥768px
 *   lg   = ≥1024px
 *   xl   = ≥1280px
 *   2xl  = ≥1536px
 *
 * Focal point format: [x%, y%] — same semantics as CSS object-position.
 *   [50, 50] = perfect center. Increase Y to push the focal point up in the
 *   rendered frame (showing more of the source's bottom).
 *
 * Usage:
 *   <FocusedImage
 *     src={img}
 *     alt="..."
 *     focal={{
 *       base: [50, 42],
 *       sm:   [52, 40],
 *       md:   [53, 36],
 *       lg:   [52, 34],
 *       xl:   [51, 36],
 *       "2xl":[50, 38],
 *     }}
 *     className="w-full h-full"
 *   />
 *
 * Only `base` is required. Missing breakpoints fall back to the previous
 * one — Tailwind's mobile-first cascade does this automatically.
 *
 * The CropFocusInspector dev-tool reads the `data-focus` attribute we emit
 * (the lg focal as a hint), so labelling stays consistent for free.
 */

export type FocalPoint = readonly [x: number, y: number];

export type FocalMap = {
  base: FocalPoint;
  sm?: FocalPoint;
  md?: FocalPoint;
  lg?: FocalPoint;
  xl?: FocalPoint;
  "2xl"?: FocalPoint;
};

const BREAKPOINT_PREFIXES = ["base", "sm", "md", "lg", "xl", "2xl"] as const;
type Breakpoint = (typeof BREAKPOINT_PREFIXES)[number];

function fpToken(prefix: Breakpoint, [x, y]: FocalPoint): string {
  // Tailwind arbitrary-value syntax: object-[X%_Y%]
  // Underscore is the canonical separator; whitespace must not appear.
  const value = `object-[${x}%_${y}%]`;
  return prefix === "base" ? value : `${prefix}:${value}`;
}

/**
 * Build the responsive Tailwind className string from a focal map.
 * Exported so other components (e.g. <video> tags that can't use this
 * wrapper directly) can stay in lock-step with the same breakpoint set.
 */
export function buildObjectPositionClasses(focal: FocalMap): string {
  const parts: string[] = [];
  for (const bp of BREAKPOINT_PREFIXES) {
    const fp = focal[bp];
    if (!fp) continue;
    parts.push(fpToken(bp, fp));
  }
  return parts.join(" ");
}

type FocusedImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "className"> & {
  focal: FocalMap;
  /** Extra Tailwind classes (sizing, aspect-ratio, etc.). object-cover is added automatically. */
  className?: string;
  /** Skip the implicit object-cover if you need object-contain or similar. */
  fit?: "cover" | "contain" | "none";
};

const FocusedImage = forwardRef<HTMLImageElement, FocusedImageProps>(
  ({ focal, className = "", fit = "cover", ...rest }, ref) => {
    const positionClasses = useMemo(() => buildObjectPositionClasses(focal), [focal]);
    const fitClass =
      fit === "cover" ? "object-cover" : fit === "contain" ? "object-contain" : "";

    // The lg (or best-available) focal becomes the inspector label hint.
    const hint = focal.lg ?? focal.md ?? focal.sm ?? focal.base;
    const dataFocus = `${hint[0]}%_${hint[1]}%`;

    return (
      <img
        ref={ref}
        data-focus={dataFocus}
        className={`${fitClass} ${positionClasses} ${className}`.trim().replace(/\s+/g, " ")}
        {...rest}
      />
    );
  }
);

FocusedImage.displayName = "FocusedImage";

export default FocusedImage;
